import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { interiorFloor, roomSpace } from '../state/interiorFloor';
import { buildSolids, fitKey, pushOut } from '../state/interiorSolids';
import { roomWalker } from '../state/roomBody';
import { cutsceneBridge } from '../city/cutscene';
import { stopAtWalls } from '../state/walls';

// SolidClamp — indoors, furniture is in the way (2026-10-31).
//
// Khari: "you can walk through the bar and step onto chairs." FloorClamp knows
// where the floor is and nothing else; this knows where the furniture is
// (state/interiorSolids: read from the room model's own tags) and pushes you
// back out of it, so you slide along a counter and round a table instead of
// through them. Never onto a chair either: you cannot reach one to step up.
//
// Runs after the walk and before FloorClamp, so the floor is probed where you
// actually end up. Same stand-down rules as FloorClamp — whoever owns the
// camera owns it alone: not while sitting (SeatCamera's glide into a seat runs
// through the chair and the table on purpose), not in freecam, not while the
// rig flies you in, not with bounds turned off.
//
// ?solids=1 outlines every footprint at its height, to see what is solid.
//
// ── WALLS (2026-11-01) ───────────────────────────────────────────────────────
// Khari: "the classroom walls upstairs (and in most places) allow me to walk
// halfway through them." Nothing indoors was a wall: FloorClamp stops you only
// where the floor ends, and a room's floor runs under its walls to their OUTER
// face — so in every room you could walk into a wall until half of you was
// through it. Furniture footprints are boxes the builder tagged; walls are not,
// and a downloaded room has no tags at all. So walls are asked of the room's own
// geometry, like the floor: each step casts one ray along it at torso height
// (WALL_H over the feet — above any stair, table or step, so those are left to
// the floor and the footprints) and stops you WALL_R short of the first
// near-vertical surface, sliding you along it. It cannot trap you: a mesh is hit
// from its front only, so from inside a wall you walk out freely. It never
// stands in a doorway: a hit in or beside a door's trigger (state/doorZones,
// published by Portals) is not a wall, so a glass or painted door still takes
// you through. Hidden meshes (a painted view taken down) and light effects (no
// depth write) are not walls. ?walls=0 turns it off; so does rooms.js
// `walls: false` for one room. The rule itself: state/walls stopAtWalls.

const R = 0.11;   // ≈24cm at this world's scale (1u ≈ 2.2m): a person's half-width
const SHOW = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('solids') != null;
const NO_WALLS = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('walls') === '0';


function outline(solids) {
  const pts = [];
  for (const s of solids) {
    const y = s.y1 + 0.01;
    if (s.r != null) {
      for (let i = 0; i < 24; i++) {
        const a = (i / 24) * Math.PI * 2; const b = ((i + 1) / 24) * Math.PI * 2;
        pts.push(s.cx + Math.cos(a) * s.r, y, s.cz + Math.sin(a) * s.r, s.cx + Math.cos(b) * s.r, y, s.cz + Math.sin(b) * s.r);
      }
    } else {
      const vx = -s.uz; const vz = s.ux;
      const c = [[1, 1], [1, -1], [-1, -1], [-1, 1]].map(([a, b]) => [s.cx + s.ux * s.hu * a + vx * s.hv * b, s.cz + s.uz * s.hu * a + vz * s.hv * b]);
      for (let i = 0; i < 4; i++) { const p = c[i]; const q = c[(i + 1) % 4]; pts.push(p[0], y, p[1], q[0], y, q[1]); }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  const lines = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: '#ff4fd8', depthTest: false, transparent: true }));
  lines.renderOrder = 999;
  return lines;
}

export default function SolidClamp({ eye = 0.75 }) {
  const camera = useThree((s) => s.camera);
  const interior = useWorldStore((s) => s.interior);
  const enabled = useWorldStore((s) => s.boundsEnabled);
  const seated = useWorldStore((s) => s.seated);
  const freeCam = useWorldStore((s) => s.freeCam);
  const camSettled = useWorldStore((s) => s.camSettled);
  const built = useRef({ model: null, key: '', solids: [], drawn: null });
  const p = useRef(new THREE.Vector3());
  const w = useRef({ prev: null });   // where the walker stood after last frame's clamp


  const clearDrawn = () => {
    const d = built.current.drawn;
    if (d) { d.parent?.remove(d); d.geometry.dispose(); d.material.dispose(); built.current.drawn = null; }
  };
  useEffect(() => () => clearDrawn(), []);
  useEffect(() => { built.current = { ...built.current, model: null, key: '', solids: [] }; clearDrawn(); w.current.prev = null; }, [interior]);

  useFrame(() => {
    const model = interiorFloor.object;
    const space = roomSpace.object;
    if (!interior || !model || !space) return;

    // Footprints per room, rebuilt if the room is refitted or rescaled.
    const key = fitKey(model, space);
    const b = built.current;
    if (b.model !== model || b.key !== key) {
      b.model = model; b.key = key;
      b.solids = buildSolids(model, space, ROOMS[interior]);
      console.info(`[solids] ${interior}: ${b.solids.length} solid${b.solids.length === 1 ? '' : 's'}${b.solids.length ? ` (${[...new Set(b.solids.map((s) => s.name.replace(/_[a-z]?\d+.*$/, '')))].slice(0, 8).join(', ')})` : ''}`);
      clearDrawn();
      if (SHOW && b.solids.length) { b.drawn = outline(b.solids); space.add(b.drawn); }
    }
    // …a scene has the camera, or you are sitting, flying, or being flown in.
    if (!enabled || seated || freeCam || !camSettled || cutsceneBridge.active) { w.current.prev = null; return; }

    // The point that walks: the body in third person (state/roomBody), else the camera.
    const P = roomWalker(camera);
    // Walls first (the step as it was taken), then the furniture.
    if (!NO_WALLS && ROOMS[interior]?.walls !== false) {
      if (w.current.prev) stopAtWalls(model, w.current.prev, P, P.y - eye);
    }
    if (b.solids.length) {
      // Into room space (rigid: position and yaw), out of anything solid, back.
      const at = space.worldToLocal(p.current.copy(P));
      if (pushOut(at, at.y - eye, b.solids, R)) {
        space.localToWorld(at);
        P.x = at.x;
        P.z = at.z;
      }
    }
    if (!w.current.prev) w.current.prev = new THREE.Vector3();
    w.current.prev.copy(P);
  });

  return null;
}
